import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';
import { config } from '../config';
import type { Locale } from '@qm/shared';
import uz from './locales/uz.json';

export const i18n = i18next;

/*
 * В основной бандл входит только узбекский словарь (язык по умолчанию, экраны до входа).
 * Каракалпакский и русский загружаются отдельными чанками при выборе языка — это ~15 КБ (gzip) меньше
 * при первом запуске. Полноту всех трёх словарей гарантирует scripts/i18n-check.mjs, поэтому
 * цепочка запасных языков не нужна для отсутствующих строк.
 */
const loaders: Record<Locale, () => Promise<Record<string, unknown>>> = {
  uz: async () => uz,
  kaa: () => import('./locales/kaa.json').then((m) => m.default),
  ru: () => import('./locales/ru.json').then((m) => m.default),
};
const loading = new Map<Locale, Promise<void>>();

/** Загрузить словарь языка (один раз). Безопасно вызывать заранее — например, сразу при запуске. */
export function ensureLocale(locale: Locale): Promise<void> {
  let p = loading.get(locale);
  if (!p) {
    p = loaders[locale]().then((res) => {
      i18next.addResourceBundle(locale, 'translation', res, true, false);
    });
    loading.set(locale, p);
  }
  return p;
}

/** Сменить язык интерфейса, предварительно загрузив словарь. */
export async function setLanguage(locale: Locale): Promise<void> {
  await ensureLocale(locale).catch(() => undefined); // нет сети — останемся на текущих строках/запасном языке
  await i18next.changeLanguage(locale);
}

void i18next.use(initReactI18next).init({
  resources: { uz: { translation: uz } },
  lng: 'uz',
  // Пока словарь выбранного языка загружается — показываем узбекские строки.
  fallbackLng: { default: ['uz'] },
  interpolation: { escapeValue: false, defaultVariables: { appName: config.appName } },
  returnNull: false,
});

/** Переопределения строк из админ-панели (поверх файлов локализации). Ошибка загрузки не критична. */
export async function loadOverrides(locale: string) {
  try {
    const res = await fetch(`${config.apiUrl}/i18n/${locale}/overrides`);
    if (!res.ok) return;
    const data = (await res.json()) as { strings: Record<string, string> };
    for (const [key, value] of Object.entries(data.strings)) {
      i18next.addResource(locale, 'translation', key, value);
    }
  } catch {
    /* оффлайн или API недоступен — используем файлы локализации */
  }
}

i18next.on('languageChanged', (lng) => {
  document.documentElement.lang = lng === 'kaa' ? 'kaa' : lng;
  void loadOverrides(lng);
});
